import React, { useCallback, useEffect, useRef, useState } from 'react';
import A11yDialog from 'a11y-dialog';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { isIosDevice } from 'utils/environment';
import { ToggleContainerContext } from 'context/toggleContainer';

import ToggleContainerButton from './button';

import styles from './toggleContainer.css';

const lockScroll = (modalElement) => {
  if (isIosDevice()) {
    import('body-scroll-lock').then((bodyScrollLock) => {
      bodyScrollLock.disableBodyScroll(modalElement);
    });
  } else {
    document.body.style.overflow = 'hidden';
  }
};

const unlockScroll = (modalElement) => {
  if (isIosDevice()) {
    import('body-scroll-lock').then((bodyScrollLock) => {
      bodyScrollLock.enableBodyScroll(modalElement);
    });
  } else {
    document.body.style.overflow = 'auto';
  }
};

const ToggleContainer = ({
  children,
  modalTitle,
  className,
  interaction,
  button }) => {
  const dialogElementRef = useRef();
  const allyDialogRef = useRef();
  const [isOpened, setIsOpened] = useState(false);
  const hasOpenedPreviously = useRef(false);
  const containerClasses = classNames(
    styles.wrapper,
    className,
    styles[interaction],
  );

  const close = useCallback(() => {
    allyDialogRef.current.hide();
    unlockScroll(dialogElementRef.current);
  }, []);
  const show = useCallback(() => {
    const onShowed = () => {
      dialogElementRef.current.removeEventListener('transitionend', onShowed);
      hasOpenedPreviously.current = true;
      setIsOpened(true);
    };
    dialogElementRef.current.addEventListener('transitionend', onShowed);
    allyDialogRef.current.show();
    lockScroll(dialogElementRef.current);
  }, []);

  useEffect(() => {
    const onClose = () => setIsOpened(false);
    if (dialogElementRef.current) {
      allyDialogRef.current = new A11yDialog(dialogElementRef.current);
      dialogElementRef.current.addEventListener('hide', onClose);
    }
    return () => {
      if (dialogElementRef.current) {
        dialogElementRef.current.removeEventListener('hide', onClose);
      }
    };
  }, []);

  return (
    <>
      <ToggleContainerButton
        label={button.label}
        icon={button.icon}
        showModal={show}
        targetElement="search-form"
      />
      <div
        aria-label={modalTitle}
        ref={dialogElementRef}
        className={containerClasses}
        /* We need to see aria-hidden initially to hide the dialog,
           but then we should leave its control to a11y-dialog once it has been used
           at least once. */
        {...(!hasOpenedPreviously.current ? { 'aria-hidden': true } : {})}
      >
        <div role="document" className={styles.content}>
          <span className={styles.buttonWrapper}>
            <button
              onClick={close}
              className={styles.dismissButton}
              aria-label={`Dismiss ${modalTitle}`}
              data-a11y-dialog-hide
            >
              <svg
                className={styles.xIcon}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>
          </span>
          <ToggleContainerContext.Provider value={{ close, isOpened }}>
            {children}
          </ToggleContainerContext.Provider>
        </div>
      </div>
    </>
  );
};

ToggleContainer.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  button: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOf(['search', 'hamburger']).isRequired,
  }).isRequired,
  modalTitle: PropTypes.string.isRequired,
  interaction: PropTypes.oneOf([
    'fromLeft',
    'fromRight',
    'fade',
  ]).isRequired,
};

ToggleContainer.defaultProps = {
  className: '',
};

const wrapWithStyles = withStyles(styles);

export default wrapWithStyles(ToggleContainer);
